import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { cn, isHoD, canReassignTasks } from "../../lib/utils";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Separator } from "../ui/separator";
import {
  LayoutDashboard,
  ShoppingBag,
  Package,
  Users,
  ClipboardList,
  BarChart3,
  Settings,
  LogOut,
  Factory,
  Truck,
  Printer,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  CalendarDays,
  ListTodo,
  ListChecks,
  Tag,
  Shield,
  Repeat,
  Trash2,
  FileEdit,
  Clipboard,
} from "lucide-react";

const navItems = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/orders", icon: ClipboardList, label: "Orders" },
  { path: "/items", icon: Package, label: "Items" },
  { path: "/categories", icon: Tag, label: "Categories" },
  { path: "/ilm", icon: Repeat, label: "ILM" },
  { path: "/customers", icon: ShoppingBag, label: "Customers" },
  { path: "/tasks", icon: Factory, label: "All Tasks" },
  { path: "/tasks/my", icon: ListTodo, label: "My Tasks" },
  { path: "/reports", icon: BarChart3, label: "Reports" },
];

const hodItems = [
  { path: "/tasks/delegate", icon: UserCheck, label: "Delegate Tasks" },
];

const superAdminItems = [
  { path: "/users", icon: Users, label: "Users" },
  { path: "/admin/task-templates", icon: ListChecks, label: "Task Templates" },
  { path: "/admin/deletion-requests", icon: Trash2, label: "Deletion Requests" },
  { path: "/admin/order-edit-requests", icon: FileEdit, label: "Edit Requests" },
  { path: "/admin/target-date-requests", icon: CalendarDays, label: "Date Change Requests" },
  { path: "/admin/security", icon: Shield, label: "Security & Backup" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

const adminItems = [
  { path: "/admin/task-templates", icon: ListChecks, label: "Task Templates" },
  { path: "/admin/deletion-requests", icon: Trash2, label: "My Deletion Requests" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

const departmentIcons = {
  merchandizing: ShoppingBag,
  prepress: Printer,
  production: Factory,
  logistics: Truck,
  packaging: Package,
};

export const Sidebar = ({ collapsed, setCollapsed }) => {
  const { user, logout, isAdmin, isSuperAdmin } = useAuth();
  const location = useLocation();

  const DeptIcon = departmentIcons[user?.department] || Factory;
  const showDelegation = canReassignTasks(user?.role);

  const roleMenuItems = isSuperAdmin ? superAdminItems : isAdmin ? adminItems : [];

  // Production Log: visible to production dept, production_hod, production_manager, admin, super_admin
  const showProductionLog = isAdmin || user?.role === "production_hod" || user?.role === "production_manager" || user?.department === "production";

  // Access control: Prepress should NOT see Orders; Prepress & Production team should NOT see Categories/Customers
  const userRole = user?.role || "";
  const userDept = user?.department || "";
  const isPrepress = userRole === "prepress_hod" || userRole === "prepress_manager" || userDept === "prepress";
  const isProduction = userRole === "production_hod" || userRole === "production_manager" || userDept === "production";

  const filteredNavItems = navItems.filter((item) => {
    // Prepress users: no Orders
    if (item.path === "/orders" && isPrepress && !isAdmin) return false;
    // Prepress & Production: no Categories, no Customers
    if (item.path === "/categories" && (isPrepress || isProduction) && !isAdmin) return false;
    if (item.path === "/customers" && (isPrepress || isProduction) && !isAdmin) return false;
    return true;
  });

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 h-full bg-background border-r border-border z-50 flex flex-col transition-all duration-300",
        collapsed ? "w-16" : "w-64"
      )}
      data-testid="sidebar"
    >
      {/* Header */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-border">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <img src="/pacfo-logo.png" alt="Pacfo" className="h-8 object-contain" />
            <span className="font-heading text-lg tracking-tight">POTS</span>
          </div>
        )}
        {collapsed && (
          <img src="/pacfo-logo.png" alt="Pacfo" className="h-6 object-contain mx-auto" />
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className={cn("h-8 w-8", collapsed && "hidden")}
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-2">
          {filteredNavItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Production Log - visible to production dept, admins */}
        {showProductionLog && (
          <>
            <Separator className="my-4" />
            <ul className="space-y-1 px-2">
              <li>
                <NavLink
                  to="/production-log"
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )
                  }
                  data-testid="nav-production-log"
                >
                  <Clipboard className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span>Production Log</span>}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/production-reports"
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )
                  }
                  data-testid="nav-production-reports"
                >
                  <BarChart3 className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span>Production Reports</span>}
                </NavLink>
              </li>
            </ul>
          </>
        )}

        {/* HoD/Admin Delegation Section */}
        {showDelegation && (
          <>
            <Separator className="my-4" />
            <ul className="space-y-1 px-2">
              {hodItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors",
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )
                    }
                    data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </>
        )}

        {isAdmin && (
          <>
            <Separator className="my-4" />
            <ul className="space-y-1 px-2">
              {roleMenuItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors",
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )
                    }
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </>
        )}
      </nav>

      {/* User Profile */}
      <div className="border-t border-border p-4">
        <div className={cn("flex items-center", collapsed ? "justify-center" : "gap-3")}>
          <Avatar className="h-9 w-9">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback className="bg-primary text-primary-foreground text-xs">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate capitalize">
                {user?.role === "super_admin" ? "Super Admin" : user?.role?.replace("_", " ")}
              </p>
            </div>
          )}
          {!collapsed && (
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </aside>
  );
};
